import {
  isOwnerSupportGhost,
  readOwnerSupportGhostFlags,
} from "../../../utils/isOwnerSupportGhost.js";

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_30_DAYS_MS = 30 * DAY_MS;

/**
 * One adsUsers child, shaped for the owner table. Ghost flags are copied when
 * the node has them; the email domain is enough when it does not.
 */
export const toAccountUserRecord = ({ uid, userData = {} }) => {
  const data = userData && typeof userData === "object" ? userData : {};

  return {
    uid,
    email: data.email,
    name: data.name,
    role: data.role,
    isDeleted: Boolean(data.isDeleted),
    lastLogins: data.lastLogins || [],
    ...readOwnerSupportGhostFlags(data),
  };
};

/**
 * Users, last activity, and counts for one account.
 * Support-session ghosts are left out of every figure. Deleted real users stay
 * in the list and still contribute logins, but they are not active users.
 * `daysSinceLastLogin` is full elapsed days, the same count the table already shows.
 */
export const summarizeAccountUsers = ({
  userRecords = [],
  now = Date.now(),
  thirtyDaysAgo = now - WINDOW_30_DAYS_MS,
} = {}) => {
  const users = [];
  let totalLoginsLast30Days = 0;
  let latestLoginTimestamp = null;
  let activeUsers = 0;
  const records = Array.isArray(userRecords) ? userRecords : [];

  for (const record of records) {
    if (!record || isOwnerSupportGhost(record)) continue;

    const lastLogins = record.lastLogins || [];
    const deleted = record.isDeleted === true;

    if (!deleted) {
      activeUsers += 1;
    }

    const recentLogins = lastLogins.filter(
      (timestamp) => timestamp > thirtyDaysAgo
    );
    totalLoginsLast30Days += recentLogins.length;

    if (lastLogins.length > 0) {
      const userLatestLogin = Math.max(...lastLogins);
      if (
        latestLoginTimestamp == null ||
        userLatestLogin > latestLoginTimestamp
      ) {
        latestLoginTimestamp = userLatestLogin;
      }
    }

    users.push({
      ...record,
      isDeleted: deleted,
      lastLogins,
      recentLogins,
      totalRecentLogins: recentLogins.length,
    });
  }

  const daysSinceLastLogin =
    latestLoginTimestamp == null
      ? null
      : Math.floor((now - latestLoginTimestamp) / DAY_MS);

  return {
    users,
    totalLoginsLast30Days,
    daysSinceLastLogin,
    activeUsers,
    latestLoginTimestamp,
  };
};
